import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { REGIONS, OPERATION_TYPES } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  company_name: "",
  region: "",
  operation_type: "",
  email: "",
  phone: "",
  peak_demand: "",
  annual_consumption: "",
  diesel_consumption: "",
  existing_solar: "",
  typical_outage_duration: "",
  required_backup_duration: "",
  expansion_plans: "",
  target_project_date: "",
  message: "",
};

const inputCls = "w-full bg-white border border-borderSubtle rounded-xl px-4 py-3 text-ink focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/15 transition-all duration-300 placeholder:text-inkMuted";

const FieldLabel = ({ label, optional }) => (
  <label className="text-sm text-inkSoft mb-2 block">
    {label}
    {optional && <span className="ml-1.5 text-xs font-normal text-inkMuted">(Optional)</span>}
  </label>
);

const Select = ({ testid, label, name, value, onChange, options }) => (
  <div>
    <FieldLabel label={label} />
    <select data-testid={testid} name={name} value={value} onChange={onChange} required className={inputCls}>
      <option value="" disabled>
        Select&hellip;
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

const Field = ({ testid, label, name, value, onChange, type = "text", required = true, placeholder }) => (
  <div>
    <FieldLabel label={label} optional={!required} />
    <input
      data-testid={testid}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className={inputCls}
    />
  </div>
);

const STEP_LABELS = {
  1: "STEP 1 — YOUR OPERATION",
  2: "STEP 2 — ENERGY PROFILE",
};

export const ContactSection = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Request received — we'll be in touch within two business days.");
      setForm(initialForm);
      setStep(1);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "object" ? detail?.message : detail;
      if (typeof detail === "object" && detail?.code === "LEAD_SAVED_EMAIL_FAILED") {
        toast.success(message || "Request received — we'll be in touch within two business days.");
        setForm(initialForm);
        setStep(1);
      } else {
        toast.error(message || "Something went wrong. Please try again or email us directly.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-baseLight px-6 py-[30px] sm:py-[40px] lg:px-12 lg:py-[70px]">
      <div className="lg:max-w-[1280px] lg:mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="-mb-[10px] lg:col-span-5 lg:mb-0"
        >
          <h2 className="font-heading text-[30px] sm:text-[35px] lg:text-[41px] font-semibold text-ink leading-tight mb-6">
            Request a Farm Energy Assessment
          </h2>
          <p className="text-[15px] text-inkSoft leading-relaxed">
            Tell us about your operation, current energy use and backup requirements. We'll use
            the information to understand whether
            <br className="hidden lg:block" /> a hybrid system is worth evaluating for your
            site.
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={handleFormSubmit}
          data-testid="contact-form"
          className="mb-[5px] sm:mb-0 lg:col-span-7 bg-white rounded-3xl border border-borderLight p-6 lg:p-10"
        >
          <div className="flex items-center justify-between mb-6">
            <span data-testid="contact-step-indicator" className="font-mono text-xs uppercase tracking-[0.1em] text-teal">
              {STEP_LABELS[step]}
            </span>
            <span className="text-xs text-inkMuted">{step} / 2</span>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step-1"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25 }}
                data-testid="contact-step-1"
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                <Field testid="contact-input-name" label="Full Name" name="full_name" value={form.full_name} onChange={handleChange} />
                <Field testid="contact-input-company" label="Farm / Company Name" name="company_name" value={form.company_name} onChange={handleChange} />
                <Select testid="contact-select-region" label="Location / BC Region" name="region" value={form.region} onChange={handleChange} options={REGIONS} />
                <Select testid="contact-select-operation" label="Facility Type" name="operation_type" value={form.operation_type} onChange={handleChange} options={OPERATION_TYPES} />
                <Field testid="contact-input-email" label="Email" name="email" type="email" value={form.email} onChange={handleChange} />
                <Field testid="contact-input-phone" label="Phone" name="phone" value={form.phone} onChange={handleChange} required={false} placeholder="Optional" />

                <div className="sm:col-span-2 flex justify-start sm:justify-end">
                  <button
                    data-testid="contact-button-continue"
                    type="submit"
                    className="flex h-10 items-center gap-2 bg-teal text-white text-sm font-medium rounded-full px-8 hover:bg-tealHover transition-all duration-300 hover:-translate-y-0.5"
                  >
                    Continue <ArrowRight size={15} />
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="step-2"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.25 }}
                data-testid="contact-step-2"
                className="grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                <Field testid="contact-input-peak-load" label="Peak Load (kW)" name="peak_demand" value={form.peak_demand} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-annual-consumption" label="Annual Electricity Consumption (kWh)" name="annual_consumption" value={form.annual_consumption} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-diesel" label="Diesel Consumption" name="diesel_consumption" value={form.diesel_consumption} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-solar" label="Existing Solar Capacity (kW)" name="existing_solar" value={form.existing_solar} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-outage" label="Typical Outage Duration" name="typical_outage_duration" value={form.typical_outage_duration} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-backup" label="Required Backup Duration" name="required_backup_duration" value={form.required_backup_duration} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-expansion" label="Expansion Plans" name="expansion_plans" value={form.expansion_plans} onChange={handleChange} required={false} placeholder="Optional" />
                <Field testid="contact-input-target-date" label="Target Project Date" name="target_project_date" value={form.target_project_date} onChange={handleChange} required={false} placeholder="Optional" />

                <div className="sm:col-span-2">
                  <FieldLabel label="Additional Project Details" optional />
                  <textarea
                    data-testid="contact-input-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Optional — tell us more about your current energy setup and challenges"
                    className={inputCls}
                  />
                </div>

                <div className="sm:col-span-2 flex items-center justify-between">
                  <button
                    data-testid="contact-button-back"
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex items-center gap-2 text-sm font-medium text-inkSoft hover:text-ink transition-colors duration-300"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={loading}
                    className="flex h-10 items-center gap-2 bg-teal text-white text-sm font-medium rounded-full px-8 hover:bg-tealHover transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
                    {loading ? "Sending" : "Request My Energy Assessment"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.form>
      </div>
    </section>
  );
};
